import classNames from 'classnames';

import { AddSystemDialog } from 'components/Dialog';
import { Button } from 'components/Button';
import { Tooltip } from 'components/Tooltip';
import { HeaderDropdown } from 'components/Layout/HeaderDropdown';
import { UserRole } from 'models';
import { setSelectedFacility, setSelectedSystem, standardApi, useAppDispatch, useAppSelector, useSystem } from 'store';
import { getFacilityName, userIsInternalAdmin } from 'utils';

import type { Facility, Healthsystem } from 'models';

interface SystemFacilitySelectorProps {
	variant?: 'header' | 'dashboard' | 'sidebar';
	showAddSystem?: boolean;
	className?: string;
}

export function SystemFacilitySelector({
	variant = 'header',
	showAddSystem = false,
	className,
}: SystemFacilitySelectorProps) {
	const dispatch = useAppDispatch();
	const { selectedFacility, selectedSystem } = useAppSelector((state) => state.userState);
	const { isLoading, data, isFetching } = useSystem();

	const isAdmin = userIsInternalAdmin(data?.user);
	const isHealthSystemAdmin = data?.user.role === UserRole.healthsystem_admin;
	const userFacilities = data?.user?.facilities.map((e) => e.id) ?? [];
	const system = data?.healthsystems.find((h) => h.id === selectedSystem);
	const facilities = data?.facilities;
	const facility = facilities?.find((f) => f.id === selectedFacility);
	const compact = variant === 'dashboard' || variant === 'sidebar';

	const dropdownClassName = classNames(
		compact && 'bg-blue-50 rounded-lg',
		variant === 'sidebar' && 'w-full xl:mr-0 [&_[data-selector-trigger]]:w-full [&_[data-selector-trigger]]:ml-0'
	);

	return (
		<div
			className={classNames(
				'flex items-center gap-1',
				variant === 'sidebar' ? 'flex-col items-stretch' : 'flex-row',
				className
			)}
		>
			{isAdmin && (
				<div className={classNames('flex items-center gap-1', variant === 'sidebar' && 'w-full')}>
					<HeaderDropdown
						activeText={system?.name ?? (isLoading ? '(Loading...)' : '(None)')}
						filterFn={(item: Healthsystem, searchQuery: string) => {
							return item.name.toLowerCase().includes(searchQuery.toLowerCase());
						}}
						className={dropdownClassName}
						label={
							compact ? (
								<span className='hidden xl:inline text-gray-500'></span>
							) : (
								<>
									<span className='hidden xl:inline'>Health System</span>
									<span className='xl:hidden'>System</span>
								</>
							)
						}
						onSelect={(option: Healthsystem) => {
							// When selecting a new health system, always completely reset the cache.
							if (option.id !== selectedSystem) {
								dispatch(setSelectedSystem(option.id));
								dispatch(standardApi.util.resetApiState());
							}
						}}
						// Filter out health systems that have ! in index 0; the ! means archived.
						options={
							data?.healthsystems.filter((hs) => {
								return !(hs.name.at(0) === '!');
							}) ?? []
						}
						renderTextFn={(item: Healthsystem) => item.name}
						hideUnderline={compact}
					/>

					{showAddSystem && (
						<AddSystemDialog>
							<Button variant='primary-ghost' sizeX='square' circle>
								<Tooltip content={'Create Health System'} triggerType='span'>
									<span className='material-symbol-sm'>domain_add</span>
								</Tooltip>
							</Button>
						</AddSystemDialog>
					)}
				</div>
			)}

			{facilities && (
				<HeaderDropdown
					activeText={isLoading || isFetching ? '(Loading...)' : (facility && getFacilityName(facility)) ?? '(None)'}
					filterFn={(item: Facility, searchQuery: string) => {
						return getFacilityName(item).toLowerCase().includes(searchQuery.toLowerCase());
					}}
					className={dropdownClassName}
					label={
						compact ? (
							<span className='hidden xl:inline text-gray-500'></span>
						) : (
							<>
								<span className='hidden xl:inline'>Your facility is set to</span>
								<span className='xl:hidden'>Facility</span>
							</>
						)
					}
					onSelect={(option: Facility) => {
						dispatch(setSelectedFacility(option.id));
					}}
					isWorking={isLoading || isFetching}
					options={
						isLoading || isFetching
							? []
							: facilities.filter((e) => userFacilities?.includes(e.id) || isAdmin || isHealthSystemAdmin)
					}
					renderTextFn={(item: Facility) => getFacilityName(item)}
					hideUnderline={compact}
				/>
			)}
		</div>
	);
}

export default SystemFacilitySelector;
